import { css } from 'lit';

export const knowledgeStyles = css`
      :host {
        display: block;
      }

      /* ─── Page Header ─── */
      .page-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: var(--space-8);
      }

      .page-header-text {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .page-title {
        font-family: var(--font-display);
        font-size: var(--text-4xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: -0.04em;
      }

      .page-description {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .header-actions {
        display: flex;
        gap: var(--space-3);
      }

      /* ─── Stats Row ─── */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-8);
      }

      .stat-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        align-items: center;
        gap: var(--space-4);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .stat-icon {
        width: 44px;
        height: 44px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .stat-icon.documents {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }

      .stat-icon.size {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }

      .stat-icon.agents {
        background: var(--color-info-light);
        color: var(--color-info);
      }

      .stat-content {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      /* ─── Two-Column Layout ─── */
      .content-layout {
        display: grid;
        grid-template-columns: 240px 1fr;
        gap: var(--space-6);
        min-height: 600px;
      }

      /* ─── Sidebar ─── */
      .sidebar {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-lg);
        padding: var(--space-4);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        height: fit-content;
        position: sticky;
        top: var(--space-4);
        overflow: hidden;
      }

      .sidebar-title {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        padding: var(--space-2) var(--space-3);
        margin-bottom: var(--space-1);
      }

      .folder-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-px);
      }

      .folder-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-base);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        user-select: none;
      }

      .folder-item:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .folder-item.active {
        background: var(--color-primary-light);
        color: var(--color-primary);
        font-weight: var(--font-medium);
      }

      .folder-item i {
        width: 18px;
        text-align: center;
        font-size: var(--text-sm);
        flex-shrink: 0;
      }

      .folder-name {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .folder-count {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        background: var(--color-bg-tertiary);
        padding: 1px 6px;
        border-radius: var(--radius-full);
        min-width: 22px;
        text-align: center;
      }

      .folder-item.active .folder-count {
        background: var(--color-primary-subtle);
        color: var(--color-primary);
      }

      /* ─── Main Content ─── */
      .main-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }

      /* ─── Upload Drop Zone ─── */
      .drop-zone {
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-8) var(--space-6);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-3);
        cursor: pointer;
        transition: all var(--transition-base);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .drop-zone:hover {
        border-color: var(--color-primary);
        background: var(--color-primary-light);
      }

      .drop-zone-icon {
        font-size: 2rem;
        color: var(--color-text-tertiary);
        transition: color var(--transition-base);
      }

      .drop-zone:hover .drop-zone-icon {
        color: var(--color-primary);
      }

      .drop-zone-text {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        text-align: center;
      }

      .drop-zone-text strong {
        color: var(--color-primary);
      }

      .drop-zone-hint {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-disabled);
      }

      /* ─── Content Toolbar ─── */
      .content-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .content-toolbar-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .content-toolbar-right {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .results-count {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .view-toggle-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        border-radius: var(--radius-md);
        color: var(--color-text-tertiary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
      }

      .view-toggle-btn:hover {
        color: var(--color-text-primary);
        border-color: var(--color-border-secondary);
      }

      .view-toggle-btn.active {
        background: var(--color-accent-light);
        color: var(--color-accent);
        border-color: var(--color-accent-subtle);
      }

      /* ─── Document Grid ─── */
      .doc-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: var(--space-4);
      }

      .doc-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        transition: all var(--transition-base);
        cursor: pointer;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .doc-card:hover {
        box-shadow: var(--shadow-elevated);
        border-color: var(--color-border-secondary);
        transform: scale(1.005);
      }

      .doc-card-header {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
      }

      .doc-type-icon {
        width: 40px;
        height: 40px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.125rem;
        flex-shrink: 0;
      }

      .doc-type-icon.pdf {
        background: #FEF2F2;
        color: var(--color-error);
      }

      .doc-type-icon.docx {
        background: #EFF6FF;
        color: var(--color-info);
      }

      .doc-type-icon.txt {
        background: var(--color-bg-tertiary);
        color: var(--color-text-tertiary);
      }

      .doc-type-icon.url {
        background: #F0FDF4;
        color: var(--color-success);
      }

      .doc-info {
        flex: 1;
        min-width: 0;
      }

      .doc-name {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 2px;
      }

      .doc-meta {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .doc-meta-sep {
        width: 3px;
        height: 3px;
        border-radius: var(--radius-full);
        background: var(--color-text-disabled);
      }

      .doc-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--space-3);
        border-top: 1px solid var(--color-border-subtle);
      }

      .doc-footer-left {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .doc-agents-count {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-agents-count i {
        font-size: var(--text-xs);
      }

      .doc-actions {
        display: flex;
        gap: var(--space-1);
      }

      .doc-action-btn {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-xs);
      }

      .doc-action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-text-primary);
      }

      .doc-action-btn.danger:hover {
        background: var(--color-error-light);
        color: var(--color-error);
      }

      /* ─── Document List View ─── */
      .doc-list {
        display: flex;
        flex-direction: column;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-lg);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .doc-list-item {
        display: grid;
        grid-template-columns: 40px 1fr 80px 100px 120px 80px 100px;
        gap: var(--space-3);
        align-items: center;
        padding: var(--space-3) var(--space-4);
        border-bottom: 1px solid var(--color-border-subtle);
        transition: background var(--transition-fast);
        cursor: pointer;
      }

      .doc-list-item:last-child {
        border-bottom: none;
      }

      .doc-list-item:hover {
        background: var(--color-bg-hover);
      }

      .doc-list-name {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .doc-list-size {
        font-family: var(--font-mono);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        text-align: right;
      }

      .doc-list-date {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-list-agents {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .doc-list-actions {
        display: flex;
        gap: var(--space-1);
        justify-content: flex-end;
      }

      /* ─── Processing badge spin ─── */
      .badge-warning i.fa-spinner-third {
        animation: spin 1s linear infinite;
      }

      /* ─── Responsive ─── */
      @media (max-width: 1024px) {
        .stats-row {
          grid-template-columns: 1fr;
        }

        .content-layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          position: static;
        }

        .folder-list {
          flex-direction: row;
          flex-wrap: wrap;
          gap: var(--space-2);
        }

        .folder-item {
          padding: var(--space-2) var(--space-3);
        }
      }

      @media (max-width: 768px) {
        .page-header {
          flex-direction: column;
          gap: var(--space-3);
        }

        .header-actions {
          width: 100%;
        }

        .header-actions .btn {
          flex: 1;
        }

        .doc-grid {
          grid-template-columns: 1fr;
        }

        .doc-list-item {
          grid-template-columns: 40px 1fr 80px 100px;
        }

        .doc-list-size,
        .doc-list-date,
        .doc-list-actions {
          display: none;
        }
      }
`;
